import { useState } from 'react'
import { ToggleButtonGroup, ToggleButton } from '@/components/ToggleButtonGroup'
import { ButtonSize } from '@/components/Button'
import { IconName } from '@/components/Icon'

const sizes: ButtonSize[] = ['xs', 'sm', 'md', 'lg']
const sampleIcon: IconName = 'Sparkle'

export function ToggleButtonGroupSandbox () {
	const [singleValue, setSingleValue] = useState<string>('option1')
	const [multipleValue, setMultipleValue] = useState<string[]>([
		'option1',
		'option3'
	])

	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Single selection (interactive) ── */}
			<Section title='Single Selection'>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<ToggleButtonGroup
						type='single'
						value={singleValue}
						onValueChange={v => setSingleValue(v as string)}
					>
						<ToggleButton value='option1' label='Option 1' />
						<ToggleButton value='option2' label='Option 2' />
						<ToggleButton value='option3' label='Option 3' />
					</ToggleButtonGroup>
					<span className='spaces:text-12 spaces:text-content-4'>
						Selected: <span className='spaces:text-content-2'>{singleValue}</span>
					</span>
				</div>
			</Section>

			{/* ── Multiple selection (interactive) ── */}
			<Section title='Multiple Selection'>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<ToggleButtonGroup
						type='multiple'
						value={multipleValue}
						onValueChange={v => setMultipleValue(v as string[])}
					>
						<ToggleButton value='option1' label='Option 1' />
						<ToggleButton value='option2' label='Option 2' />
						<ToggleButton value='option3' label='Option 3' />
						<ToggleButton value='option4' label='Option 4' />
					</ToggleButtonGroup>
					<span className='spaces:text-12 spaces:text-content-4'>
						Selected:{' '}
						<span className='spaces:text-content-2'>
							{multipleValue.length ? multipleValue.join(', ') : 'none'}
						</span>
					</span>
				</div>
			</Section>

			{/* ── Sizes ── */}
			<Section title='Sizes (single, solid)'>
				<div className='spaces:flex spaces:flex-col spaces:items-start spaces:gap-12'>
					{sizes.map(s => (
						<div key={s} className='spaces:flex spaces:items-center spaces:gap-16'>
							<span className='spaces:text-12 spaces:text-content-3 spaces:w-16 spaces:shrink-0'>{s}</span>
							<ToggleButtonGroup type='single' value='a' size={s}>
								<ToggleButton value='a' label='Alpha' />
								<ToggleButton value='b' label='Beta' />
								<ToggleButton value='c' label='Gamma' />
							</ToggleButtonGroup>
						</div>
					))}
				</div>
			</Section>

			{/* ── Container appearance ── */}
			<Section title='Container Appearance'>
				<div className='spaces:flex spaces:flex-col spaces:items-start spaces:gap-12'>
					<div className='spaces:flex spaces:items-center spaces:gap-16'>
						<span className='spaces:text-12 spaces:text-content-3 spaces:w-40 spaces:shrink-0'>solid</span>
						<ToggleButtonGroup type='single' value='a' appearance='solid'>
							<ToggleButton value='a' label='Alpha' />
							<ToggleButton value='b' label='Beta' />
							<ToggleButton value='c' label='Gamma' />
						</ToggleButtonGroup>
					</div>
					<div className='spaces:flex spaces:items-center spaces:gap-16'>
						<span className='spaces:text-12 spaces:text-content-3 spaces:w-40 spaces:shrink-0'>outline</span>
						<ToggleButtonGroup type='single' value='a' appearance='outline'>
							<ToggleButton value='a' label='Alpha' />
							<ToggleButton value='b' label='Beta' />
							<ToggleButton value='c' label='Gamma' />
						</ToggleButtonGroup>
					</div>
				</div>
			</Section>

			{/* ── Orientation ── */}
			<Section title='Orientation'>
				<div className='spaces:flex spaces:items-start spaces:gap-32'>
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>horizontal</span>
						<ToggleButtonGroup type='single' value='a' orientation='horizontal'>
							<ToggleButton value='a' label='One' />
							<ToggleButton value='b' label='Two' />
							<ToggleButton value='c' label='Three' />
						</ToggleButtonGroup>
					</div>
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>vertical</span>
						<ToggleButtonGroup type='single' value='a' orientation='vertical'>
							<ToggleButton value='a' label='One' />
							<ToggleButton value='b' label='Two' />
							<ToggleButton value='c' label='Three' />
						</ToggleButtonGroup>
					</div>
				</div>
			</Section>

			{/* ── Icon variants ── */}
			<Section title='Icon Variants'>
				<div className='spaces:flex spaces:flex-wrap spaces:items-start spaces:gap-24'>
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>iconLeft</span>
						<ToggleButtonGroup type='single' value='a'>
							<ToggleButton value='a' label='Alpha' iconLeft={sampleIcon} />
							<ToggleButton value='b' label='Beta' iconLeft={sampleIcon} />
							<ToggleButton value='c' label='Gamma' iconLeft={sampleIcon} />
						</ToggleButtonGroup>
					</div>
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>iconRight</span>
						<ToggleButtonGroup type='single' value='a'>
							<ToggleButton value='a' label='Alpha' iconRight={sampleIcon} />
							<ToggleButton value='b' label='Beta' iconRight={sampleIcon} />
							<ToggleButton value='c' label='Gamma' iconRight={sampleIcon} />
						</ToggleButtonGroup>
					</div>
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>iconOnly</span>
						<ToggleButtonGroup type='single' value='a'>
							<ToggleButton value='a' iconOnly={sampleIcon} />
							<ToggleButton value='b' iconOnly={sampleIcon} />
							<ToggleButton value='c' iconOnly={sampleIcon} />
						</ToggleButtonGroup>
					</div>
				</div>
			</Section>

			{/* ── Disabled items ── */}
			<Section title='Disabled Items'>
				<ToggleButtonGroup type='single' value='a'>
					<ToggleButton value='a' label='Active' />
					<ToggleButton value='b' label='Disabled' disabled />
					<ToggleButton value='c' label='Normal' />
				</ToggleButtonGroup>
			</Section>
		</div>
	)
}

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-12'>
			<h3 className='spaces:text-16 spaces:font-semi spaces:text-content-1'>{title}</h3>
			{children}
		</div>
	)
}
